import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, Paper, Alert } from '@mui/material';
import { useAuth } from '../../hooks/useAuth';
import { authService, WhoAmIResponse } from '../../services/AuthService';
import { LoadingScreen } from '../common/LoadingScreen';

/**
 * Debug component for testing authentication integration
 * Shows current auth state and allows testing API calls
 */
export const AuthDebug: React.FC = () => {
  const { isAuthenticated, user, isLoading } = useAuth();
  const [whoamiData, setWhoamiData] = useState<WhoAmIResponse | null>(null);
  const [isTestingAuth, setIsTestingAuth] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);

  const testAuthentication = async () => {
    console.log('Test authentication button clicked!');
    setIsTestingAuth(true);
    setAuthError(null);
    
    try {
      console.log('Calling authService.whoAmI()...');
      const response = await authService.whoAmI();
      console.log('Got response:', response);
      setWhoamiData(response);
    } catch (error: unknown) {
      console.error('Auth test failed:', error);
      const errorMessage = error instanceof Error 
        ? error.message 
        : (error as { response?: { data?: { message?: string } } })?.response?.data?.message || 'Authentication test failed';
      setAuthError(errorMessage);
    } finally {
      setIsTestingAuth(false);
    }
  };

  // Auto-test on mount if authenticated
  useEffect(() => {
    if (isAuthenticated && !isLoading) {
      testAuthentication();
    }
  }, [isAuthenticated, isLoading]);

  if (isLoading) {
    return <LoadingScreen message="Loading authentication state..." />;
  }

  return (
    <Paper sx={{ p: 3, m: 2 }}>
      <Typography variant="h6" gutterBottom>
        Authentication Debug Info
      </Typography>
      
      <Box sx={{ mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Frontend Auth State:
        </Typography>
        <Typography variant="body1">
          Authenticated: {isAuthenticated ? 'Yes' : 'No'}
        </Typography>
        {user && (
          <>
            <Typography variant="body1">
              User: {user.name || user.email}
            </Typography>
            <Typography variant="body1">
              Email: {user.email}
            </Typography>
          </>
        )}
      </Box>

      <Button 
        variant="contained" 
        onClick={testAuthentication}
        disabled={isTestingAuth}
        sx={{ mb: 2 }}
      >
        {isTestingAuth ? 'Testing...' : 'Test Backend Auth'}
      </Button>

      {authError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {authError}
        </Alert>
      )}

      {whoamiData && (
        <Box>
          <Typography variant="body2" color="text.secondary">
            Backend Auth Response:
          </Typography>
          <pre style={{ 
            backgroundColor: '#f5f5f5', 
            color: '#333',
            padding: '10px', 
            borderRadius: '4px',
            overflow: 'auto',
            fontSize: '12px'
          }}>
            {JSON.stringify(whoamiData, null, 2)}
          </pre>
        </Box>
      )}
    </Paper>
  );
};